export const ADMIN_DEV_ARCHITECTURE_PIPELINE = [
  {
    id: "data-model",
    title: "Data Model & Custom Objects",
    icon: "database",
    color: "#f59e0b",
    summary: "Every Admin + Dev solution starts with the right Standard/Custom Objects and Relationships.",
    details: [
      "Standard Objects (Account, Contact, Opportunity, Case) ship pre-built; Custom Objects (always suffixed __c) model business-specific data like Shipment__c.",
      "Relationships (Lookup, Master-Detail, Junction Objects) determine ownership, cascading delete behavior, and whether Roll-Up Summary fields are available.",
      "Page Layouts and Record Types then decide what each user group actually sees on top of this data model.",
    ],
  },
  {
    id: "security-sharing",
    title: "Security & Sharing",
    icon: "fingerprint",
    color: "#fbbf24",
    summary: "Organization-Wide Defaults, Role Hierarchy, and Sharing Rules control exactly who can see which records.",
    details: [
      "OWD sets the most restrictive baseline per object; everything else (Role Hierarchy, Sharing Rules, Manual Sharing) only ADDS visibility on top.",
      "Profiles define the outer boundary of object/field/app access; Permission Sets grant additional, often temporary, access without changing a user's Profile.",
      "This layer must be designed before any automation is built on top of it — automation inherits the running user's visibility unless explicitly run in system context.",
    ],
  },
  {
    id: "automation",
    title: "Automation — Flow, Approval & Legacy Workflow",
    icon: "zap",
    color: "#fb923c",
    summary: "Lightning Flow (Screen & Record-Triggered) is the modern standard; Approval Processes handle multi-step sign-off.",
    details: [
      "Record-Triggered Flows fire before/after save like a trigger, without code; Screen Flows guide users through interactive multi-step data entry.",
      "Approval Processes route records through one or more approval steps with their own criteria and approver, locking the record while pending.",
      "Workflow Rules and Process Builder still exist in many orgs (maintenance mode) — understanding them is essential for maintaining legacy automation.",
    ],
  },
  {
    id: "apex-triggers",
    title: "Apex & Triggers",
    icon: "layers",
    color: "#fbbf24",
    summary: "When declarative automation hits its limits, bulk-safe Apex Triggers (with a Handler class) take over.",
    details: [
      "Exactly one trigger per object, delegating all logic to a Trigger Handler class — before-triggers modify records in memory, after-triggers act on related records.",
      "Bulkification (collect Ids into a Set, one SOQL query, build a Map, loop in memory only) is non-negotiable — never SOQL/DML inside a for loop.",
      "Custom Settings and Custom Metadata Types externalize configurable thresholds and deployable reference data out of hardcoded Apex.",
    ],
  },
  {
    id: "async-apex",
    title: "Asynchronous Apex",
    icon: "rocket",
    color: "#f97316",
    summary: "Batch, Schedulable, Future and Queueable Apex handle large volumes and long-running work outside the save transaction.",
    details: [
      "Batch Apex processes massive record sets in chunks, each chunk getting fresh governor limits — the right tool for org-wide nightly recalculations.",
      "Queueable Apex (preferred over @future) supports job chaining and complex parameter types for external callouts.",
      "Schedulable Apex kicks off recurring jobs (like a nightly Batch run) via a Cron expression.",
    ],
  },
  {
    id: "ui-visualforce",
    title: "Visualforce & Custom UI",
    icon: "filter",
    color: "#eab308",
    summary: "When Lightning pages aren't enough, Visualforce (MVC: markup + Controller/Extension) builds fully custom UI.",
    details: [
      "A Custom Controller or Controller Extension bridges the page to Apex logic; Wrapper Classes combine multi-object data for complex grids.",
      "@RemoteAction / apex:actionFunction let JavaScript call Apex directly without a full page postback — essential for inline, live-editing UIs.",
      "Still relevant for legacy orgs and specific print/PDF/complex-UI use cases the standard Lightning framework doesn't cover.",
    ],
  },
  {
    id: "integration",
    title: "Integration & APIs",
    icon: "bot",
    color: "#facc15",
    summary: "REST/SOAP APIs, OAuth 2.0, and the Bulk/Streaming APIs connect Salesforce to the outside world in both directions.",
    details: [
      "@RestResource Apex web services let external systems call INTO Salesforce; Apex HTTP callouts let Salesforce call OUT to external APIs.",
      "OAuth 2.0 (via a Connected App) issues access tokens without ever sharing a raw password — the standard for both directions.",
      "The Bulk API handles millions of records asynchronously; the Streaming API / Platform Events push near-real-time event notifications.",
    ],
  },
  {
    id: "deployment",
    title: "Deployment & DevOps",
    icon: "building",
    color: "#facc15",
    summary: "Everything built above must move safely from Sandbox to Production via Change Sets or SFDX/CI-CD.",
    details: [
      "Sandbox types (Developer, Partial Copy, Full Copy) trade off refresh frequency against how much production data they mirror.",
      "SFDX (Salesforce CLI) enables source-driven, version-controlled, CI/CD-friendly deployment — the modern standard over point-and-click Change Sets.",
      "A disciplined migration checklist (test coverage, peer review, rollback plan) is what separates a safe production deploy from a risky one.",
    ],
  },
];
